import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { useTheme } from './ThemeContext';
import { Lock, User } from 'lucide-react';

const ProfileSettings = () => {
  const { user, token, logout } = useAuth();
  const { currentTheme } = useTheme();
  const [activeSection, setActiveSection] = useState('password');
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  
  // Username change state
  const [usernameForm, setUsernameForm] = useState({
    newUsername: '',
    password: ''
  });
  const [usernameLoading, setUsernameLoading] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [usernameSuccess, setUsernameSuccess] = useState('');

  const getTextClass = (type) => {
    if (currentTheme === 'dark') {
      return type === 'primary' ? 'text-white' :
             type === 'secondary' ? 'text-slate-200' :
             type === 'tertiary' ? 'text-slate-400' : 'text-white';
    } else if (currentTheme === 'grither') {
      return type === 'primary' ? 'text-white' :
             type === 'secondary' ? 'text-white/90' :
             type === 'tertiary' ? 'text-white/70' : 'text-white';
    }
    return type === 'primary' ? 'text-gray-900' :
           type === 'secondary' ? 'text-gray-700' :
           type === 'tertiary' ? 'text-gray-500' : 'text-gray-900';
  };

  const getCardClass = () => {
    return currentTheme === 'dark' ? 'modern-dark-card' :
           currentTheme === 'grither' ? 'grither-card' : 'bg-white';
  };

  const getInputClass = () => {
    return currentTheme === 'dark' 
      ? 'bg-slate-700/50 border-slate-600 text-white focus:border-blue-400 focus:ring-blue-400/50'
      : currentTheme === 'grither'
      ? 'bg-white/10 border-white/20 text-white placeholder-white/60 focus:border-white/40 focus:ring-white/20'
      : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500';
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('Заполните все поля');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('Новые пароли не совпадают');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('Новый пароль должен содержать минимум 6 символов');
      return;
    }

    setPasswordLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          old_password: passwordForm.oldPassword,
          new_password: passwordForm.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setPasswordSuccess('Пароль успешно изменен');
        setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => setPasswordSuccess(''), 5000);
      } else {
        setPasswordError(data.detail || 'Ошибка при изменении пароля');
      }
    } catch (error) {
      console.error('Password change error:', error);
      setPasswordError('Ошибка соединения с сервером');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleUsernameChange = async (e) => {
    e.preventDefault();
    setUsernameError('');
    setUsernameSuccess('');

    // Validation
    if (!usernameForm.newUsername || !usernameForm.password) {
      setUsernameError('Заполните все поля');
      return;
    }

    if (usernameForm.newUsername === user.username) {
      setUsernameError('Новое имя пользователя совпадает с текущим');
      return;
    }

    if (usernameForm.newUsername.length < 3) {
      setUsernameError('Имя пользователя должно содержать минимум 3 символа');
      return;
    }

    setUsernameLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/change-username`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          new_username: usernameForm.newUsername,
          password: usernameForm.password
        })
      });

      const data = await response.json();

      if (response.ok) {
        setUsernameSuccess('Имя пользователя успешно изменено. Вы будете перенаправлены на страницу входа...');
        setUsernameForm({ newUsername: '', password: '' });
        
        // Update token and user info
        if (data.access_token) {
          localStorage.setItem('token', data.access_token);
        }
        
        // Redirect to login after 2 seconds
        setTimeout(() => {
          logout();
        }, 2000);
      } else {
        setUsernameError(data.detail || 'Ошибка при изменении имени пользователя');
      }
    } catch (error) {
      console.error('Username change error:', error);
      setUsernameError('Ошибка соединения с сервером');
    } finally {
      setUsernameLoading(false);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Profile Info Card */}
      <div className={`rounded-xl shadow-lg p-4 md:p-6 ${getCardClass()}`}>
        <h2 className={`text-xl md:text-2xl font-bold mb-3 md:mb-4 ${getTextClass('primary')}`}>
          Профиль пользователя
        </h2>
        
        <div className="space-y-2 md:space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-3">
            <span className={`text-xs md:text-sm font-medium ${getTextClass('tertiary')}`}>
              Имя пользователя:
            </span>
            <span className={`text-sm md:text-base font-bold ${getTextClass('primary')}`}>
              {user?.username}
            </span>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-3">
            <span className={`text-xs md:text-sm font-medium ${getTextClass('tertiary')}`}>
              Email:
            </span>
            <span className={`text-sm md:text-base ${getTextClass('secondary')}`}>
              {user?.email || 'Не указан'}
            </span>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-3">
            <span className={`text-xs md:text-sm font-medium ${getTextClass('tertiary')}`}>
              Роль:
            </span>
            <span className={`text-sm md:text-base font-bold ${getTextClass('primary')}`}>
              {user?.role}
            </span>
          </div>
          
          {user?.telegram_username && (
            <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-3">
              <span className={`text-xs md:text-sm font-medium ${getTextClass('tertiary')}`}>
                Telegram:
              </span>
              <span className={`text-sm md:text-base ${getTextClass('secondary')}`}>
                @{user.telegram_username}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Settings Navigation - Mobile Friendly */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          onClick={() => setActiveSection('password')}
          className={`inline-flex items-center justify-center gap-2 w-full px-4 py-3 text-sm md:text-base font-medium rounded-lg transition-all duration-200 ${
            activeSection === 'password'
              ? currentTheme === 'dark'
                ? 'bg-blue-500 text-white'
                : 'bg-blue-600 text-white'
              : currentTheme === 'dark'
              ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          <Lock size={18} strokeWidth={2} /> <span className="hidden sm:inline">Изменить </span>Пароль
        </button>
        
        <button
          onClick={() => setActiveSection('username')}
          className={`inline-flex items-center justify-center gap-2 w-full px-4 py-3 text-sm md:text-base font-medium rounded-lg transition-all duration-200 ${
            activeSection === 'username'
              ? currentTheme === 'dark'
                ? 'bg-blue-500 text-white'
                : 'bg-blue-600 text-white'
              : currentTheme === 'dark'
              ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          <User size={18} strokeWidth={2} /> <span className="hidden sm:inline">Изменить </span>Логин
        </button>
      </div>

      {/* Change Password Section */}
      {activeSection === 'password' && (
        <div className={`rounded-xl shadow-lg p-4 md:p-6 ${getCardClass()}`}>
          <h3 className={`text-lg md:text-xl font-bold mb-3 md:mb-4 ${getTextClass('primary')}`}>
            Изменение пароля
          </h3>
          
          <form onSubmit={handlePasswordChange} className="space-y-3 md:space-y-4">
            <div>
              <label className={`block text-xs md:text-sm font-medium mb-2 ${getTextClass('secondary')}`}>
                Текущий пароль
              </label>
              <input
                type="password"
                value={passwordForm.oldPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                className={`w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border transition-all duration-200 text-base ${getInputClass()}`}
                placeholder="Введите текущий пароль"
                disabled={passwordLoading}
              />
            </div>
            
            <div>
              <label className={`block text-xs md:text-sm font-medium mb-2 ${getTextClass('secondary')}`}>
                Новый пароль
              </label>
              <input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                className={`w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border transition-all duration-200 text-base ${getInputClass()}`}
                placeholder="Введите новый пароль (минимум 6 символов)"
                disabled={passwordLoading}
              />
            </div>
            
            <div>
              <label className={`block text-xs md:text-sm font-medium mb-2 ${getTextClass('secondary')}`}>
                Подтвердите новый пароль
              </label>
              <input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                className={`w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border transition-all duration-200 text-base ${getInputClass()}`}
                placeholder="Повторите новый пароль"
                disabled={passwordLoading}
              />
            </div>

            {passwordError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-xs md:text-sm text-red-600 dark:text-red-400">
                  {passwordError}
                </p>
              </div>
            )}

            {passwordSuccess && (
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-xs md:text-sm text-green-600 dark:text-green-400">
                  {passwordSuccess}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={passwordLoading}
              className={`w-full px-4 md:px-6 py-3 md:py-4 rounded-lg font-medium text-sm md:text-base transition-all duration-200 ${
                passwordLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white hover:scale-105'
              }`}
            >
              {passwordLoading ? 'Изменение...' : 'Изменить пароль'}
            </button>
          </form>
        </div>
      )}

      {/* Change Username Section */}
      {activeSection === 'username' && (
        <div className={`rounded-xl shadow-lg p-4 md:p-6 ${getCardClass()}`}>
          <h3 className={`text-lg md:text-xl font-bold mb-3 md:mb-4 ${getTextClass('primary')}`}>
            Изменение имени пользователя
          </h3>
          
          <div className="mb-3 md:mb-4 p-3 md:p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <p className={`text-xs md:text-sm ${getTextClass('secondary')}`}>
              ⚠️ После изменения имени пользователя вы будете перенаправлены на страницу входа. 
              Используйте новое имя пользователя для входа.
            </p>
          </div>
          
          <form onSubmit={handleUsernameChange} className="space-y-3 md:space-y-4">
            <div>
              <label className={`block text-xs md:text-sm font-medium mb-2 ${getTextClass('secondary')}`}>
                Новое имя пользователя
              </label>
              <input
                type="text"
                value={usernameForm.newUsername}
                onChange={(e) => setUsernameForm({ ...usernameForm, newUsername: e.target.value })}
                className={`w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border transition-all duration-200 text-base ${getInputClass()}`}
                placeholder="Введите новое имя пользователя (минимум 3 символа)"
                disabled={usernameLoading}
              />
            </div>
            
            <div>
              <label className={`block text-xs md:text-sm font-medium mb-2 ${getTextClass('secondary')}`}>
                Подтвердите паролем
              </label>
              <input
                type="password"
                value={usernameForm.password}
                onChange={(e) => setUsernameForm({ ...usernameForm, password: e.target.value })}
                className={`w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border transition-all duration-200 text-base ${getInputClass()}`}
                placeholder="Введите текущий пароль для подтверждения"
                disabled={usernameLoading}
              />
            </div>

            {usernameError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-xs md:text-sm text-red-600 dark:text-red-400">
                  {usernameError}
                </p>
              </div>
            )}

            {usernameSuccess && (
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-xs md:text-sm text-green-600 dark:text-green-400">
                  {usernameSuccess}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={usernameLoading}
              className={`w-full px-4 md:px-6 py-3 md:py-4 rounded-lg font-medium text-sm md:text-base transition-all duration-200 ${
                usernameLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white hover:scale-105'
              }`}
            >
              {usernameLoading ? 'Изменение...' : 'Изменить имя пользователя'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ProfileSettings;
